import type { QueryClient } from "@tanstack/react-query";
import type { PostHog } from "posthog-js";
import { projectMembersQueryOptions } from "@/app/members/api/get-project-members";
import { onboardingProgressQueryOptions } from "@/app/onboarding/api/get-onboarding-progress";
import {
  isPostHogDecideBlocked,
  waitForPostHogFlags,
} from "./wait-for-posthog-flags";

export type FirstWinGateResult =
  | { kind: "skip" }
  | { kind: "redirect-to-onboarding" }
  /** Keep the user on the current route (timed-out /decide, API failure, or unknown flag). */
  | { kind: "stay" };

export async function resolveFirstWinGate({
  posthog,
  queryClient,
  organizationId,
  projectId,
  reloadAfterIdentify = false,
  waitForFlags = true,
}: {
  posthog: PostHog;
  queryClient: QueryClient;
  organizationId: string;
  projectId: string;
  reloadAfterIdentify?: boolean;
  waitForFlags?: boolean;
}): Promise<FirstWinGateResult> {
  if (waitForFlags) {
    await waitForPostHogFlags(posthog, { reloadAfterIdentify });
  }

  const flagVariant = posthog.getFeatureFlag("first-win-onboarding");

  if (flagVariant !== "test") {
    return isPostHogDecideBlocked() ? { kind: "stay" } : { kind: "skip" };
  }

  try {
    const [progress, members] = await Promise.all([
      queryClient.ensureQueryData(
        onboardingProgressQueryOptions(organizationId, projectId)
      ),
      queryClient.ensureQueryData(
        projectMembersQueryOptions(organizationId, projectId)
      ),
    ]);

    const eligible =
      progress != null &&
      !progress.dismissed &&
      !progress.account_has_executed_tool_externally &&
      members.count === 1;

    if (eligible) {
      return { kind: "redirect-to-onboarding" };
    }

    // Dismissed in test cohort — stay on /onboarding when visited directly (not skip/eject).
    if (progress?.dismissed) {
      return { kind: "stay" };
    }

    return { kind: "skip" };
  } catch {
    return { kind: "stay" };
  }
}
